import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage, useContent, useUI, useSwitchLangHref, useLocalPath } from '../context/LanguageContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { lang, prefix } = useLanguage();
  const content = useContent();
  const ui = useUI();
  const localPath = useLocalPath();
  const switchLangHref = useSwitchLangHref();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0d1117]/90 backdrop-blur-xl border-b border-[#21262d]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link to={prefix ? `${prefix}/` : '/'} className="flex items-center gap-3 group cursor-pointer">
          <div className="w-9 h-9 rounded-full border border-[#3b82f6]/50 flex items-center justify-center bg-[#3b82f6]/10">
            <span className="font-mono text-xs font-bold text-[#3b82f6]">G2</span>
          </div>
          <span className="font-mono text-sm font-semibold tracking-[0.2em] text-[#e6edf3] uppercase">
            Go2 Digital
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {content.navLinks.map((link) => {
            const fullHref = `${prefix}${link.href}`;
            return (
              <Link
                key={link.label}
                to={fullHref}
                className={`font-mono text-[11px] font-medium tracking-[0.15em] uppercase transition-colors duration-300 ${
                  location.pathname === fullHref
                    ? 'text-[#3b82f6]'
                    : 'text-[#7d8590] hover:text-[#e6edf3]'
                }`}
              >
                {link.label}
              </Link>
            );
          })}

          <Link
            to={localPath('/projects/')}
            className="font-mono text-[11px] font-medium tracking-[0.15em] text-[#3b82f6] border border-[#3b82f6]/50 rounded-full px-5 py-2.5 hover:bg-[#3b82f6]/10 transition-all duration-300 uppercase"
          >
            {ui.seeOurWork}
          </Link>

          {/* Language Switcher */}
          <Link
            to={switchLangHref}
            className="flex items-center gap-1.5 font-mono text-[11px] font-medium tracking-[0.1em] text-[#7d8590] hover:text-[#e6edf3] transition-colors duration-300 border border-[#21262d] rounded-full px-3.5 py-2 hover:border-[#3b82f6]/30"
          >
            <Globe size={12} />
            <span>{lang === 'sl' ? 'EN' : 'SL'}</span>
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden text-[#e6edf3] p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0d1117]/98 backdrop-blur-xl border-b border-[#21262d] overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {content.navLinks.map((link) => {
                const fullHref = `${prefix}${link.href}`;
                return (
                  <Link
                    key={link.label}
                    to={fullHref}
                    className={`font-mono text-xs font-medium tracking-[0.15em] transition-colors text-left uppercase ${
                      location.pathname === fullHref ? 'text-[#3b82f6]' : 'text-[#7d8590] hover:text-[#e6edf3]'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <Link
                to={localPath('/projects/')}
                className="font-mono text-xs font-medium tracking-[0.15em] text-[#3b82f6] border border-[#3b82f6]/50 rounded-full px-6 py-2.5 hover:bg-[#3b82f6]/10 transition-all duration-300 mt-2 w-fit uppercase"
              >
                {ui.seeOurWork}
              </Link>
              {/* Mobile Language Switcher */}
              <Link
                to={switchLangHref}
                className="flex items-center gap-1.5 font-mono text-xs font-medium tracking-[0.1em] text-[#7d8590] hover:text-[#e6edf3] transition-colors mt-2 w-fit"
              >
                <Globe size={12} />
                <span>{lang === 'sl' ? 'Preklopite na angle\u0161\u010dino' : 'Preklopite na sloven\u0161\u010dino'}</span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
